import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Receipt, Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SuperbillGeneratorProps {
  bookingId: string;
  practitionerId: string;
  clientId: string;
  icdCodes: string[];
  cptCodes: string[];
  disabled?: boolean;
}

export function SuperbillGenerator({
  bookingId,
  practitionerId,
  clientId,
  icdCodes,
  cptCodes,
  disabled = false,
}: SuperbillGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (icdCodes.length === 0 && cptCodes.length === 0) {
      toast.error('Please select at least one ICD-10 or CPT code before generating a superbill.');
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-superbill', {
        body: {
          bookingId,
          practitionerId,
          clientId,
          icdCodes,
          cptCodes,
        },
      });

      if (error) throw error;

      if (data?.html) {
        // Open superbill in new window for printing/saving as PDF
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(data.html);
          printWindow.document.close();
          toast.success('Superbill generated! Use browser print (Ctrl/Cmd+P) to save as PDF.');
        }
      } else {
        throw new Error('No superbill content returned');
      }
    } catch (err: any) {
      console.error('Superbill generation error:', err);
      toast.error(err.message || 'Failed to generate superbill');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Receipt className="h-4 w-4" />
          Insurance Superbill
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          Generate a superbill document with selected diagnosis and procedure codes for insurance reimbursement.
        </p>
        <Button
          onClick={handleGenerate}
          disabled={disabled || isGenerating || (icdCodes.length === 0 && cptCodes.length === 0)}
          size="sm"
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Generate Superbill
            </>
          )}
        </Button>
        {icdCodes.length === 0 && cptCodes.length === 0 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Select ICD-10/CPT codes above first
          </p>
        )}
      </CardContent>
    </Card>
  );
}
